// The overworld chart in the custom tileset (docs/70): the generator's valley read cell by cell
// into the tiles of `customTiles.ts` (water and its banks, woods, ranges, roads by shape, a feature
// for each place), and the Art Studio's "every tile" board. Pure; `CustomMapCanvas` draws both.
//
// docs/143: the World chart. Every tile of a country's folders is placed somewhere on some valley (a
// test runs the generator over many seeds per country): each place's own art on its node (`ow.arts`),
// the grounds by the country's shares, roads by the links along them (so the drawn road is the route
// the pips and the bands follow), bridges and fords straight across the water, mountain passes where a
// road climbs a ridge, farms round the settlements and a few landmarks out in the country.
import type { Overworld } from './overworld';
import { roadShape, SHORE_CORNERS, tilesFor, type ArtSeason, type Biome, type TileKind } from './customTiles';

/** One tile in a cell; `variant` pins a variant (the "every tile" board), else the cell's hash picks one. */
export interface Piece { tile: string; variant?: number }
/** A cell, bottom to top. `biome` is the folder its art comes from on a map of regions (docs/185 PB13); else the chart's. */
export interface CustomCell { ground: Piece; overlays: Piece[]; path?: Piece; feature?: Piece; biome?: Biome }
export interface CustomChart { w: number; h: number; seed: number; cells: CustomCell[] }

/** A stable hash of a cell (and a salt per layer), so a chart always picks the same variants. */
export function cellSalt(x: number, y: number, seed: number, salt: number): number {
  let h = (Math.imul(x + 1, 73856093) ^ Math.imul(y + 1, 19349663) ^ Math.imul(salt + 1, 83492791) ^ seed) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 2246822519) >>> 0;
  return (h ^ (h >>> 13)) >>> 0;
}
/** The salt per layer, so a cell's ground and its feature pick their variants apart. */
export const LAYER_SALT = { ground: 11, overlay: 12, path: 13, feature: 14 } as const;
/** The variant a piece shows in cell (x, y): its pinned one, else the cell's pick. */
export const pieceVariant = (variants: readonly number[], p: Piece, x: number, y: number, seed: number, layer: keyof typeof LAYER_SALT): number | undefined =>
  p.variant ?? variantAt(variants, x, y, seed, LAYER_SALT[layer]);
/** Which of a tile's variants a cell shows. */
export function variantAt(variants: readonly number[], x: number, y: number, seed: number, salt: number): number | undefined {
  return variants.length ? variants[cellSalt(x, y, seed, salt) % variants.length] : undefined;
}

/** Smooth value noise in 0..1 over a lattice every 4 cells (a smoothstep blend of the four corners' hashes). */
export function patchNoise(x: number, y: number, seed: number, salt: number): number {
  const gx = x / 4, gy = y / 4, ix = Math.floor(gx), iy = Math.floor(gy);
  const ease = (t: number) => t * t * (3 - 2 * t);
  const fx = ease(gx - ix), fy = ease(gy - iy);
  const v = (cx: number, cy: number) => (cellSalt(cx, cy, seed, salt) % 1000) / 1000;
  return (v(ix, iy) * (1 - fx) + v(ix + 1, iy) * fx) * (1 - fy) + (v(ix, iy + 1) * (1 - fx) + v(ix + 1, iy + 1) * fx) * fy;
}
/** The noise level under which `share` percent of cells fall: blended noise bunches toward the middle,
 *  so this is its measured quantile every 5% (0–50), between which it is read straight. */
const NOISE_QUANTILES = [0, 0.148, 0.21, 0.258, 0.3, 0.337, 0.373, 0.406, 0.438, 0.47, 0.5];
export function noiseShare(share: number): number {
  const t = Math.max(0, Math.min(50, share)) / 5, i = Math.min(9, Math.floor(t));
  return NOISE_QUANTILES[i]! + (NOISE_QUANTILES[i + 1]! - NOISE_QUANTILES[i]!) * (t - i);
}

/** Tiles that stand for somewhere people live and farm: farmland lies round them. */
const SETTLED = new Set(['town', 'village', 'castle', 'port', 'farm', 'windmill', 'inn', 'abbey']);

/**
 * How each country's open ground is shared out (docs/143 OW3), in percent of the open cells: meadow and
 * heath in rounded patches, sand on a stretch of bank (`beach`) or in dune fields (`dunes`), and how much
 * of the ground round a range is scree (`scree`, 0..1). Every country draws every ground; the art says
 * what it is there (the desert's meadow is flowering sand, the Underdark's moss).
 */
const COUNTRY: Record<Biome, { meadow: number; heath: number; beach: number; dunes: number; scree: number; reeds: number }> = {
  temperate: { meadow: 22, heath: 12, beach: 10, dunes: 0, scree: 0.35, reeds: 0.06 },
  wetland: { meadow: 24, heath: 14, beach: 10, dunes: 0, scree: 0.3, reeds: 0.16 },
  boreal: { meadow: 10, heath: 30, beach: 22, dunes: 0, scree: 0.45, reeds: 0.05 },
  desert: { meadow: 12, heath: 16, beach: 0, dunes: 26, scree: 1, reeds: 0.04 },
  highland: { meadow: 10, heath: 32, beach: 14, dunes: 0, scree: 1, reeds: 0.05 },
  jungle: { meadow: 20, heath: 10, beach: 26, dunes: 0, scree: 0.35, reeds: 0.08 },
  underdark: { meadow: 14, heath: 18, beach: 20, dunes: 0, scree: 1, reeds: 0.05 },
  ashlands: { meadow: 14, heath: 18, beach: 14, dunes: 0, scree: 1, reeds: 0.04 },
};

/** The landmarks out in the country (docs/143 OW2): at most one of each on a chart, at this chance (percent). */
const LANDMARKS: ReadonlyArray<readonly [string, number]> = [
  ['graveyard', 70], ['standing_stones', 55], ['barrow', 55], ['mine', 65], ['ruins', 45], ['camp', 45], ['lair', 40],
  ['cave', 35], ['tower', 35], ['inn', 40], ['shrine', 30], ['abbey', 25],
];

const N = 1, E = 2, S = 4, Wb = 8;
const SIDE_STEP: ReadonlyArray<readonly [number, number, number, number]> = [[0, -1, N, S], [1, 0, E, Wb], [0, 1, S, N], [-1, 0, Wb, E]];

/**
 * The valley in the custom tileset, for one country and season (docs/70, docs/143): every cell's ground,
 * banks, road and feature, from what the generator made (`ow.land`, the places and their `arts`, the
 * roads) and the country's own shares. Pure and keyed by cell, so every player sees the same chart.
 */
export function customChart(ow: Overworld, biome: Biome, _season: ArtSeason): CustomChart {
  const { w: W, h: H, land, seed } = ow;
  // docs/185 PB13: on a map of regions each cell reads its own region's country (`biome` is then the seat's)
  const regions = ow.regions && ow.regionOf ? ow.regions : undefined;
  const regionIdx = (x: number, y: number) => (regions ? ow.regionOf![Math.max(0, Math.min(H - 1, y))]!.charCodeAt(Math.max(0, Math.min(W - 1, x))) - 97 : 0);
  const biomeAt = (x: number, y: number): Biome => (regions ? regions[regionIdx(x, y)]!.biome : biome);
  const inside = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H;
  const at = (x: number, y: number) => land[y]?.[x];
  const idx = (x: number, y: number) => y * W + x;
  // off the chart's edge the country goes on as it is at the edge
  const wet = (x: number, y: number) => at(Math.max(0, Math.min(W - 1, x)), Math.max(0, Math.min(H - 1, y))) === '~';
  const hash = (x: number, y: number, salt: number) => cellSalt(x, y, seed, salt);
  const roll = (x: number, y: number, salt: number) => (hash(x, y, salt) % 1000) / 1000;
  const countryAt = (x: number, y: number) => COUNTRY[biomeAt(x, y)];
  const desertAt = (x: number, y: number) => biomeAt(x, y) === 'desert';
  const northAt = (x: number, y: number) => { const b = biomeAt(x, y); return b === 'boreal' || b === 'highland'; };
  const open = (ch: string | undefined) => ch === '.' || ch === 'x';
  const around = (x: number, y: number, test: (ch: string | undefined) => boolean, diagonals: boolean) => {
    let n = 0;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if ((!dx && !dy) || (!diagonals && dx && dy)) continue;
      if (test(at(Math.max(0, Math.min(W - 1, x + dx)), Math.max(0, Math.min(H - 1, y + dy))))) n++;
    }
    return n;
  };
  const nearMountain = (x: number, y: number) => around(x, y, (c) => c === 'M', true) > 0;
  const byWater = (x: number, y: number) => around(x, y, (c) => c === '~', false) > 0;
  const patch = (x: number, y: number, salt: number, share: number) => share > 0 && patchNoise(x, y, seed, salt) < noiseShare(share);

  // ── the places: each place's own art stands on its node cell; its pin and name sit beside it (OW1) ──
  const features = new Map<number, string>();
  const nodeCell = new Map<number, string>();
  const nodeArea = new Set<number>();
  const settled: Array<[number, number]> = [];
  for (const [key, n] of Object.entries(ow.nodes)) {
    const x = Math.floor(n.x), y = Math.floor(n.y), look = ow.looks[key];
    nodeCell.set(idx(x, y), key);
    for (let dy = -1; dy <= 1; dy++) for (let dx = -2; dx <= 2; dx++) if (inside(x + dx, y + dy)) nodeArea.add(idx(x + dx, y + dy));
    let art = ow.arts?.[key];
    if (look?.kind === 'lake' && desertAt(x, y) && art === 'reeds') art = 'oasis';
    if (art && at(x, y) !== '~') {
      features.set(idx(x, y), art);
      if (SETTLED.has(art)) settled.push([x, y]);
    }
  }
  const nodeDist = (x: number, y: number) => Math.min(99, ...Object.values(ow.nodes).map((n) => Math.hypot(Math.floor(n.x) - x, Math.floor(n.y) - y)));

  // ── the roads, by the links along them, so a road's tiles are its route and nothing else (OW5) ──
  const major = (key: string) => key === 'home' || ow.looks[key]?.kind === 'castle' || ow.looks[key]?.kind === 'town';
  const links = new Map<number, number>();
  const paved = new Set<number>();
  const link = (i: number, bit: number) => links.set(i, (links.get(i) ?? 0) | bit);
  for (const r of ow.roads) {
    const stone = (!r.a.startsWith('edge:') && major(r.a)) || (!r.b.startsWith('edge:') && major(r.b));
    const cells: Array<[number, number]> = [];
    for (let i = 0; i < r.path.length - 1; i++) {
      const [ax, ay] = r.path[i]!, [bx, by] = r.path[i + 1]!;
      const n = Math.max(Math.abs(bx - ax), Math.abs(by - ay));
      for (let s = i ? 1 : 0; s <= n; s++) cells.push([Math.floor(ax + ((bx - ax) * s) / Math.max(1, n)), Math.floor(ay + ((by - ay) * s) / Math.max(1, n))]);
    }
    if (r.path.length === 1) cells.push([Math.floor(r.path[0]![0]), Math.floor(r.path[0]![1])]);
    cells.forEach(([x, y], i) => {
      if (!inside(x, y)) return;
      const c = idx(x, y);
      if (!links.has(c)) links.set(c, 0);
      if (stone) paved.add(c);
      const next = cells[i + 1];
      if (!next) return;
      for (const [dx, dy, out, back] of SIDE_STEP) if (next[0] - x === dx && next[1] - y === dy) { link(c, out); if (inside(next[0], next[1])) link(idx(next[0], next[1]), back); }
    });
    // a road off the chart leaves by the side it ends on (a drove road by both)
    const offEdge = (end: [number, number], prev: [number, number] | undefined) => {
      const [x, y] = end, horiz = prev ? prev[1] === y : x === 0 || x === W - 1;
      const c = idx(x, y);
      if (horiz && x === W - 1) link(c, E); else if (horiz && x === 0) link(c, Wb);
      else if (y === 0) link(c, N); else if (y === H - 1) link(c, S);
      else if (x === 0) link(c, Wb); else if (x === W - 1) link(c, E);
    };
    if (r.b.startsWith('edge:') && cells.length) offEdge(cells[cells.length - 1]!, cells[cells.length - 2]);
    if (r.a.startsWith('edge:') && cells.length) offEdge(cells[0]!, cells[1]);
  }
  const sidesOf = (m: number) => [!!(m & N), !!(m & E), !!(m & S), !!(m & Wb)] as const;
  const fordPlace = Object.entries(ow.looks).filter(([, l]) => l.kind === 'ford').map(([key]) => ow.nodes[key]!);
  const stonePlace = Object.values(ow.looks).some((l) => (l.kind === 'bridge' || l.kind === 'ford') && l.stone);
  /** A span of road over water: its first cell and its length along the axis. */
  const span = (x: number, y: number, ns: boolean) => {
    const [dx, dy] = ns ? [0, 1] : [1, 0];
    let sx = x, sy = y, len = 1;
    while (wet(sx - dx, sy - dy) && links.has(idx(sx - dx, sy - dy)) && inside(sx - dx, sy - dy)) { sx -= dx; sy -= dy; }
    for (let ex = sx + dx, ey = sy + dy; inside(ex, ey) && at(ex, ey) === '~' && links.has(idx(ex, ey)); ex += dx, ey += dy) len++;
    return { sx, sy, len };
  };
  const crossing = (x: number, y: number, m: number): string => {
    const ns = !!(m & (N | S)) && !(m & (E | Wb));
    const { sx, sy, len } = span(x, y, ns);
    const ford = fordPlace.some((n) => Math.hypot(Math.floor(n.x) - x, Math.floor(n.y) - y) < 2.5) || (!paved.has(idx(sx, sy)) && len === 1 && hash(sx, sy, 21) % 3 === 0);
    const kind = ford ? 'ford' : paved.has(idx(sx, sy)) || stonePlace ? 'stone_bridge' : 'wooden_bridge';
    return `${kind}_${ns ? 'ns' : 'ew'}`;
  };

  // ── a seat on a crossing (Elkford) has its town on the bank beside the ford or bridge ──
  const seat = ow.nodes.home;
  if (seat && at(Math.floor(seat.x), Math.floor(seat.y)) === '~') {
    const x0 = Math.floor(seat.x), y0 = Math.floor(seat.y);
    let best: [number, number] | undefined, bd = 3;
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
      const x = x0 + dx, y = y0 + dy, i = idx(x, y), d = Math.hypot(dx, dy * 1.1);
      if (inside(x, y) && open(at(x, y)) && !links.has(i) && !features.has(i) && d < bd) { bd = d; best = [x, y]; }
    }
    if (best) { features.set(idx(...best), 'town'); settled.push(best); }
  }

  // ── ground ──
  const cells: CustomCell[] = [];
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const ch = at(x, y)!, i = idx(x, y);
    const cell: CustomCell = { ground: { tile: 'plains' }, overlays: [] };
    if (ch === '~') {
      // open water: no land within a cell (diagonals too), and none two cells off straight across
      const deep = around(x, y, (c) => c !== '~', true) === 0 && [[-2, 0], [2, 0], [0, -2], [0, 2]].every(([dx, dy]) => wet(x + dx!, y + dy!));
      cell.ground.tile = deep ? 'deep_water' : 'water';
      for (const [k2, [dx, dy]] of ([['n', [0, -1]], ['e', [1, 0]], ['s', [0, 1]], ['w', [-1, 0]]] as const)) if (!wet(x + dx, y + dy)) cell.overlays.push({ tile: `shore_${k2}` });
      for (const c of SHORE_CORNERS) {
        const dy = c[0] === 'n' ? -1 : 1, dx = c[1] === 'e' ? 1 : -1;
        if (wet(x + dx, y) && wet(x, y + dy) && !wet(x + dx, y + dy)) cell.overlays.push({ tile: `shore_${c}` });
      }
    } else if (ch === 'b') cell.ground.tile = biomeAt(x, y) === 'underdark' && around(x, y, (c) => c === 'b', false) === 4 ? 'abyss' : 'marsh';
    else if (ch === 'M') cell.ground.tile = northAt(x, y) && around(x, y, (c) => c === 'M', false) === 4 ? 'snowfield' : 'barren';
    else {
      const country = countryAt(x, y);
      const node = nodeCell.has(i);
      const d = Math.min(99, ...settled.map(([sx, sy]) => Math.hypot(sx - x, sy - y)));
      if (open(ch) && !node && d < 3.2 && (d < 1.9 || patchNoise(x, y, seed, 5) < 0.45)) cell.ground.tile = 'farmland';
      else if (nearMountain(x, y) && roll(x, y, 6) < country.scree) cell.ground.tile = 'barren';
      else if (country.beach && byWater(x, y) && patch(x, y, 8, country.beach * 1.6)) cell.ground.tile = 'sand';
      else if (patch(x, y, 4, country.dunes)) cell.ground.tile = 'sand';
      else if (patch(x, y, 2, country.heath)) cell.ground.tile = 'heath';
      else if (patch(x, y, 3, country.meadow)) cell.ground.tile = 'meadow';
    }
    if (regions) cell.biome = biomeAt(x, y);
    cells.push(cell);
  }
  const groundAt = (x: number, y: number) => cells[idx(x, y)]?.ground.tile;

  // ── the seat's harbour: a seat (or a village, now and then) by the water has a port on the bank beside it (OW2) ──
  for (const [key, n] of Object.entries(ow.nodes)) {
    const art = ow.arts?.[key], x0 = Math.floor(n.x), y0 = Math.floor(n.y);
    if (art === 'port' || !(key === 'home' || (art === 'village' && hash(x0, y0, 22) % 2 === 0))) continue;
    let best: [number, number] | undefined, bd = 3.2;
    for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) {
      const x = x0 + dx, y = y0 + dy, i = idx(x, y), d = Math.hypot(dx, dy);
      if (!inside(x, y) || !open(at(x, y)) || nodeArea.has(i) || links.has(i) || features.has(i) || !byWater(x, y) || wet(x, y - 1)) continue;
      if (d < bd) { bd = d; best = [x, y]; }
    }
    if (best) { features.set(idx(...best), 'port'); cells[idx(...best)]!.ground.tile = groundAt(...best) === 'farmland' ? 'plains' : groundAt(...best)!; }
  }

  // ── what stands on the land ──
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = idx(x, y), ch = at(x, y)!;
    if (features.has(i)) continue;
    const m = links.get(i);
    let f: string | undefined;
    if (ch === 'M') f = m !== undefined ? 'mountain_pass' : around(x, y, (c) => c === 'M', false) === 4 ? 'peaks' : around(x, y, (c) => c === 'M', false) === 0 ? 'crags' : 'mountain';
    else if (m !== undefined) f = undefined;
    else if (ch === 'T') {
      const deep = around(x, y, (c) => c === 'T', true) === 8;
      const edge = around(x, y, (c) => c === 'T' || c === 'p', true) <= 4;
      const b = biomeAt(x, y);
      const conifer = b === 'boreal' ? patch(x, y, 9, 45) : b === 'highland' ? patch(x, y, 9, 25) : false;
      f = edge && roll(x, y, 10) < 0.4 ? 'copse' : conifer ? 'pine_forest' : deep ? 'deep_forest' : 'forest';
    } else if (ch === 'p') f = desertAt(x, y) && roll(x, y, 11) < 0.6 ? 'scrub' : 'pine_forest';
    else if (ch === 'a') f = desertAt(x, y) && roll(x, y, 11) < 0.5 ? 'scrub' : 'copse';
    else if (ch === 'b') { if (cells[i]!.ground.tile !== 'abyss') { const r = roll(x, y, 5); f = r < 0.12 ? 'swamp_wood' : r < 0.3 ? 'reeds' : undefined; } }
    else if (nodeArea.has(i)) f = undefined;
    else if (open(ch) && nearMountain(x, y) && hash(x, y, 6) % 3 !== 0) f = 'hills';
    else if (open(ch) && byWater(x, y) && roll(x, y, 12) < (desertAt(x, y) ? 0.14 : countryAt(x, y).reeds)) f = desertAt(x, y) ? 'oasis' : 'reeds';
    else if (open(ch)) {
      const g = cells[i]!.ground.tile, r = roll(x, y, 13);
      if (g === 'heath' && biomeAt(x, y) === 'highland' && r < 0.08) f = 'hills';
      else if ((g === 'heath' || g === 'barren' || g === 'sand') && r < 0.07) f = 'scrub';
      else if (desertAt(x, y) && g === 'plains' && r < 0.035) f = 'scrub';
      else if ((g === 'heath' || g === 'barren') && r > 0.975) f = 'crags';
      else if (g === 'plains' && r > 0.988) f = 'copse';
    }
    if (f) features.set(i, f);
  }

  // ── the farms round the settlements: orchards beside them, a farmstead or two, one mill ──
  const farmAll = cells.map((_, i) => i).filter((i) => cells[i]!.ground.tile === 'farmland' && !features.has(i) && !links.has(i) && !nodeArea.has(i));
  farmAll.sort((a, b) => hash(a % W, Math.floor(a / W), 14) - hash(b % W, Math.floor(b / W), 14));
  // so many of each on a chart; on a map of regions, in each region
  for (let r = 0; r < (regions?.length ?? 1); r++) {
    const farm = regions ? farmAll.filter((i) => regionIdx(i % W, Math.floor(i / W)) === r) : farmAll;
    const want = { orchard: 2, farm: 2, windmill: 1 };
    for (const i of farm) {
      const x = i % W, y = Math.floor(i / W);
      const d = Math.min(99, ...settled.map(([sx, sy]) => Math.hypot(sx - x, sy - y)));
      const pick = d < 2.5 && want.orchard ? 'orchard' : d >= 2.2 && want.farm && hash(x, y, 15) % 2 ? 'farm' : d >= 2 && want.windmill ? 'windmill' : undefined;
      if (!pick || [...features].some(([j, t]) => t === pick && Math.hypot((j % W) - x, Math.floor(j / W) - y) < 3)) continue;
      features.set(i, pick);
      want[pick as keyof typeof want]--;
    }
  }

  // ── the landmarks: one of each now and then, out where it belongs, apart from the places and each other ──
  const marks: Array<[number, number]> = [];
  const free = (i: number, replace: readonly string[] = []) => !links.has(i) && !nodeArea.has(i) && (!features.has(i) || replace.includes(features.get(i)!));
  const fits: Record<string, (x: number, y: number, i: number) => boolean> = {
    graveyard: (x, y, i) => free(i) && open(at(x, y)) && settled.some(([sx, sy]) => Math.hypot(sx - x, sy - y) < 3.3),
    standing_stones: (x, y, i) => free(i, ['scrub', 'crags']) && open(at(x, y)) && /heath|meadow|plains|barren/.test(groundAt(x, y)!) && nodeDist(x, y) > 3.5,
    barrow: (x, y, i) => free(i, ['scrub']) && open(at(x, y)) && /heath|plains|meadow/.test(groundAt(x, y)!) && nodeDist(x, y) > 3.5,
    mine: (x, y, i) => free(i, ['hills']) && features.get(i) === 'hills',
    ruins: (x, y, i) => free(i, ['scrub', 'crags', 'copse']) && open(at(x, y)) && nodeDist(x, y) > 4,
    camp: (x, y, i) => free(i) && open(at(x, y)) && around(x, y, (c) => c === 'T' || c === 'p', false) > 0 && nodeDist(x, y) > 3.5,
    lair: (x, y, i) => free(i, ['deep_forest', 'mountain']) && (features.get(i) === 'deep_forest' || (features.get(i) === 'mountain' && !wet(x, y + 1) && open(at(x, y + 1)))),
    cave: (x, y, i) => free(i, ['mountain']) && features.get(i) === 'mountain' && open(at(x, y + 1)),
    tower: (x, y, i) => free(i, ['hills']) && (features.get(i) === 'hills' || (open(at(x, y)) && !features.has(i))) && [...SIDE_STEP].some(([dx, dy]) => links.has(idx(x + dx, y + dy))),
    inn: (x, y, i) => free(i) && open(at(x, y)) && [...SIDE_STEP].some(([dx, dy]) => inside(x + dx, y + dy) && links.has(idx(x + dx, y + dy))) && nodeDist(x, y) > 5,
    shrine: (x, y, i) => free(i, ['scrub']) && open(at(x, y)) && [...SIDE_STEP].some(([dx, dy]) => inside(x + dx, y + dy) && links.has(idx(x + dx, y + dy))) && nodeDist(x, y) > 3.5,
    abbey: (x, y, i) => free(i, ['scrub']) && open(at(x, y)) && /meadow|heath|plains/.test(groundAt(x, y)!) && nodeDist(x, y) > 4.5,
  };
  // at most one of each on a chart; on a map of regions, in each region
  for (let r = 0; r < (regions?.length ?? 1); r++) LANDMARKS.forEach(([tile, chance], n) => {
    if (cellSalt(n, r, seed, 30) % 100 >= chance) return;
    // inside the chart's frame, so a landmark isn't cut by the rim
    const order = cells.map((_, i) => i).filter((i) => i % W >= 1 && i % W < W - 1 && i >= W && i < W * (H - 1) && (!regions || regionIdx(i % W, Math.floor(i / W)) === r)).sort((a, b) => hash(a % W, Math.floor(a / W), 40 + n) - hash(b % W, Math.floor(b / W), 40 + n));
    for (const i of order) {
      const x = i % W, y = Math.floor(i / W);
      if (!fits[tile]!(x, y, i) || marks.some(([mx, my]) => Math.hypot(mx - x, my - y) < 3)) continue;
      features.set(i, tile);
      if (cells[i]!.ground.tile === 'farmland') cells[i]!.ground.tile = 'plains';
      marks.push([x, y]);
      return;
    }
  });

  // ── paths and features into the cells ──
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = idx(x, y), cell = cells[i]!, m = links.get(i), f = features.get(i);
    if (m !== undefined) {
      const [n, e, s, w] = sidesOf(m);
      const sides = +n + +e + +s + +w;
      if (at(x, y) === '~') cell.path = { tile: crossing(x, y, m) };
      // a road that ends at a place stops at its art; one that runs on through it is drawn under it
      else if (!(f && nodeCell.has(i) && sides <= 1)) cell.path = { tile: `${paved.has(i) ? 'stone_road' : 'dirt_track'}_${roadShape(n, e, s, w)}` };
    }
    if (f) cell.feature = { tile: f };
  }
  return { w: W, h: H, seed, cells };
}

/** Where each entry of the "every tile" board stands. */
export interface SpecimenSpot { x: number; y: number; tile: string; variant: number }

/**
 * Every tile and every variant of a folder on one board (the Studio's "every tile" view): each in
 * its own cell with a cell of plains round it, a new row for each group, on the ground it belongs
 * on (bridges and shores on water, reeds and swamp wood in the marsh, the oasis in sand).
 */
export function specimenChart(biome: Biome, variantsOf: (tile: string) => readonly number[], w: number): { chart: CustomChart; spots: SpecimenSpot[] } {
  const spots: SpecimenSpot[] = [];
  let x = 1, y = 1, group: string | undefined;
  const tiles = tilesFor(biome);
  for (const t of tiles) {
    if (group !== undefined && group !== t.group) { x = 1; y += 2; }
    group = t.group;
    for (const v of variantsOf(t.id).length ? variantsOf(t.id) : [1]) {
      if (x >= w - 1) { x = 1; y += 2; }
      spots.push({ x, y, tile: t.id, variant: v });
      x += 2;
    }
  }
  const h = y + 2;
  const plains = variantsOf('plains')[0] ?? 1;
  const cells: CustomCell[] = Array.from({ length: w * h }, () => ({ ground: { tile: 'plains', variant: plains }, overlays: [] }));
  const kinds = new Map<string, TileKind>(tiles.map((t) => [t.id, t]));
  for (const s of spots) {
    const k = kinds.get(s.tile)!, cell = cells[s.y * w + s.x]!, piece = { tile: s.tile, variant: s.variant };
    if (k.layer === 'ground') cell.ground = piece;
    else if (k.layer === 'overlay') { cell.ground = { tile: 'water', variant: variantsOf('water')[0] ?? 1 }; cell.overlays.push(piece); }
    else if (k.layer === 'path') {
      if (/bridge|ford/.test(s.tile)) cell.ground = { tile: 'water', variant: variantsOf('water')[0] ?? 1 };
      cell.path = piece;
    } else {
      if (s.tile === 'reeds' || s.tile === 'swamp_wood') cell.ground = { tile: 'marsh', variant: variantsOf('marsh')[0] ?? 1 };
      if (s.tile === 'oasis') cell.ground = { tile: 'sand', variant: variantsOf('sand')[0] ?? 1 };
      cell.feature = piece;
    }
  }
  return { chart: { w, h, seed: 1, cells }, spots };
}

/** Every tile a chart draws (pinned variants aside). */
export function chartTileIds(c: CustomChart): Set<string> {
  const out = new Set<string>();
  for (const cell of c.cells) for (const p of [cell.ground, ...cell.overlays, cell.path, cell.feature]) if (p) out.add(p.tile);
  return out;
}
